/* Store + venue locator.
 *
 * Two datasets, one switch: retail stock harvested from Stockist, and the bars
 * and restaurants pouring Gėris. Both render through the same map, list and
 * filters, so switching swaps the data and the labels and nothing else.
 *
 * Mapbox GL and the data files load on first visibility, not on page load: the
 * library is ~230 KB and plenty of visitors never scroll this far. Pins live in
 * a GPU symbol layer rather than as DOM markers, which is what keeps 600+ of
 * them smooth on a phone.
 */
(function () {
  var root = document.querySelector('[data-hml]');
  if (!root) return;

  var CFG = window.HML_CONFIG || {};
  var S = CFG.strings || {};
  var GL_JS = 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js';
  var GL_CSS = 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css';
  var PAGE = 40;                 // list rows rendered per batch
  var LT_CENTER = [23.95, 55.20];
  var LT_ZOOM = 6.1;

  var els = {
    seg: root.querySelector('[data-hml-seg]'),
    thumb: root.querySelector('[data-hml-thumb]'),
    search: root.querySelector('[data-hml-search]'),
    near: root.querySelector('[data-hml-near]'),
    chips: root.querySelector('[data-hml-chips]'),
    count: root.querySelector('[data-hml-count]'),
    list: root.querySelector('[data-hml-list]'),
    map: root.querySelector('[data-hml-map]'),
    loading: root.querySelector('[data-hml-loading]')
  };

  var state = {
    tab: 'retail',
    data: {},                    // tab -> { locations, chains }
    chain: null,
    query: '',
    origin: null,                // [lng, lat] once the visitor shares location
    shown: PAGE,
    activeId: null,
    map: null,
    popup: null,
    ready: false
  };

  /* helpers --------------------------------------------------------------- */
  var ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return ESCAPES[c]; });
  }
  function fold(s) {
    // Diacritics folded so "silute" finds "Šilutė" and "gintarine" finds the pharmacy.
    return String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  function km(a, b) {
    var R = 6371, dLat = (b[1] - a[1]) * Math.PI / 180, dLng = (b[0] - a[0]) * Math.PI / 180;
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(a[1] * Math.PI / 180) * Math.cos(b[1] * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.sqrt(s));
  }
  function fmtKm(d) { return d < 10 ? d.toFixed(1) + ' km' : Math.round(d) + ' km'; }
  function t(key, fallback, n) {
    var v = S[key] || fallback;
    return n == null ? v : v.replace('{n}', n);
  }

  /* loading --------------------------------------------------------------- */
  function loadCss(href) {
    if (document.querySelector('link[href="' + href + '"]')) return;
    var l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = href;
    document.head.appendChild(l);
  }
  function loadGl(cb) {
    if (window.mapboxgl) return cb();
    loadCss(GL_CSS);
    var s = document.createElement('script');
    s.src = GL_JS;
    s.async = true;
    s.onload = cb;
    s.onerror = fail;
    document.head.appendChild(s);
  }
  function json(url) {
    return fetch(url, { credentials: 'omit' }).then(function (r) {
      if (!r.ok) throw new Error(r.status);
      return r.json();
    });
  }
  function fail() {
    if (els.loading) els.loading.textContent = t('error', 'Žemėlapio nepavyko įkelti.');
  }

  /* data ------------------------------------------------------------------ */
  function normalise(tab, raw) {
    if (tab === 'retail') {
      return {
        locations: (raw.locations || []).map(function (r) {
          return {
            id: 'r' + r.i, name: r.n, addr: r.a, city: r.c,
            lat: r.lat, lng: r.lng, hours: r.h, pickup: r.p,
            web: r.w, tel: r.t, chains: r.f || []
          };
        }),
        chains: raw.chains || []
      };
    }
    // Venues carry the brand in `b` and have no chain taxonomy of their own, so
    // the brand doubles as the filter: "show me every Vero Cafe".
    var rows = (raw || []).map(function (p, i) {
      return {
        id: 'v' + i, name: p.b, addr: p.a, city: p.c,
        lat: p.lat, lng: p.lng, hours: '', pickup: '',
        web: '', tel: '', chains: [p.b]
      };
    });
    var seen = {};
    rows.forEach(function (r) { seen[r.name] = 1; });
    return { locations: rows, chains: Object.keys(seen).sort() };
  }

  function ensureData(tab) {
    if (state.data[tab]) return Promise.resolve(state.data[tab]);
    var url = tab === 'retail' ? CFG.retailUrl : CFG.venuesUrl;
    return json(url).then(function (raw) {
      state.data[tab] = normalise(tab, raw);
      return state.data[tab];
    });
  }

  /* filtering ------------------------------------------------------------- */
  function filtered() {
    var d = state.data[state.tab];
    if (!d) return [];
    var q = fold(state.query).trim();
    var rows = d.locations.filter(function (r) {
      if (state.chain && r.chains.indexOf(state.chain) === -1) return false;
      if (!q) return true;
      return fold(r.name + ' ' + r.addr + ' ' + r.city).indexOf(q) !== -1;
    });
    if (state.origin) {
      rows = rows.slice().map(function (r) {
        r._d = km(state.origin, [r.lng, r.lat]);
        return r;
      }).sort(function (a, b) { return a._d - b._d; });
    }
    return rows;
  }

  function rowById(id) {
    var d = state.data[state.tab];
    if (!d) return null;
    for (var i = 0; i < d.locations.length; i++) {
      if (d.locations[i].id === id) return d.locations[i];
    }
    return null;
  }

  /* rendering ------------------------------------------------------------- */
  function renderChips() {
    var d = state.data[state.tab];
    if (!d || !els.chips) return;
    // An explicit "all" chip: without it an active filter is only visible as one
    // dark pill among twelve, and it silently ANDs with whatever gets typed.
    var all = '<button class="hml__chip" type="button" data-hml-chain=""' +
      ' aria-pressed="' + (state.chain ? 'false' : 'true') + '">' + esc(t('all', 'Visos')) + '</button>';
    els.chips.innerHTML = all + d.chains.map(function (c) {
      return '<button class="hml__chip" type="button" data-hml-chain="' + esc(c) + '"' +
        ' aria-pressed="' + (state.chain === c ? 'true' : 'false') + '">' + esc(c) + '</button>';
    }).join('');
  }

  function renderList() {
    var rows = filtered();
    var slice = rows.slice(0, state.shown);

    if (els.count) {
      els.count.textContent = rows.length ? t('count', '{n} vietų', rows.length) : '';
    }
    if (!rows.length) {
      // With a chain selected the filter is usually what emptied the list, not
      // the search term, and the chip alone does not make that obvious.
      var msg = state.chain
        ? t('empty_filtered', '{n} tinkamų vietų nerasta.').replace('{n}', state.chain)
        : t('empty', 'Nieko neradome. Pabandyk kitą miestą ar gatvę.');
      els.list.innerHTML = '<p class="hml__empty">' + esc(msg) +
        (state.chain ? '<br><button class="hml__clear" type="button" data-hml-chain="">' +
          esc(t('clear', 'Rodyti visas')) + '</button>' : '') + '</p>';
      return;
    }

    var html = slice.map(function (r) {
      var meta = [];
      if (r._d != null) meta.push('<span class="hml__dist">' + fmtKm(r._d) + '</span>');
      if (r.hours) meta.push('<span>' + esc(r.hours) + '</span>');
      if (r.pickup) meta.push('<span class="hml__pick">' + esc(r.pickup) + '</span>');
      return '<button class="hml__item" type="button" data-hml-go="' + esc(r.id) + '"' +
        ' aria-current="' + (state.activeId === r.id ? 'true' : 'false') + '">' +
        '<span class="hml__name">' + esc(r.name) + '</span>' +
        '<span class="hml__addr">' + esc(r.addr) + (r.city ? ', ' + esc(r.city) : '') + '</span>' +
        (meta.length ? '<span class="hml__meta">' + meta.join('') + '</span>' : '') +
        '</button>';
    }).join('');

    if (rows.length > state.shown) {
      html += '<button class="hml__more" type="button" data-hml-more>' +
        esc(t('more', 'Rodyti daugiau ({n})', rows.length - state.shown)) + '</button>';
    }
    els.list.innerHTML = html;
  }

  function fc(rows) {
    return {
      type: 'FeatureCollection',
      features: rows.map(function (r) {
        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [r.lng, r.lat] },
          properties: {
            id: r.id, name: r.name, addr: r.addr, city: r.city,
            hours: r.hours, pickup: r.pickup
          }
        };
      })
    };
  }

  function syncMap() {
    if (!state.ready) return;
    var src = state.map.getSource('hml');
    if (src) src.setData(fc(filtered()));
  }

  function popupFor(p) {
    var html = '<div class="hml__pop-name">' + esc(p.name) + '</div>' +
      '<div class="hml__pop-addr">' + esc(p.addr) + (p.city ? ', ' + esc(p.city) : '') + '</div>';
    if (p.hours) html += '<div class="hml__pop-meta">' + esc(p.hours) + '</div>';
    if (p.pickup) html += '<div class="hml__pop-meta hml__pick">' + esc(p.pickup) + '</div>';
    var q = encodeURIComponent(p.name + ' ' + p.addr + ' ' + (p.city || ''));
    html += '<a class="hml__pop-link" href="https://www.google.com/maps/search/?api=1&query=' + q +
      '" target="_blank" rel="noopener">' + esc(t('directions', 'Kaip nuvykti')) + '</a>';
    return html;
  }

  function markActive(id) {
    state.activeId = id;
    var rows = els.list.querySelectorAll('[data-hml-go]');
    for (var i = 0; i < rows.length; i++) {
      rows[i].setAttribute('aria-current', rows[i].getAttribute('data-hml-go') === id ? 'true' : 'false');
    }
  }

  function revealInList(id) {
    // Scroll the results panel only, never the page: scrollIntoView on a row
    // inside a scrollable panel drags the whole document with it.
    var el = els.list.querySelector('[data-hml-go="' + id + '"]');
    if (!el) return;
    var delta = (el.getBoundingClientRect().top - els.list.getBoundingClientRect().top)
              - (els.list.clientHeight - el.clientHeight) / 2;
    els.list.scrollTo({ top: els.list.scrollTop + delta, behavior: 'smooth' });
  }

  function showPoint(r, fromMap) {
    if (state.popup) state.popup.remove();
    state.popup = new window.mapboxgl.Popup({ offset: 26, maxWidth: '260px' })
      .setLngLat([r.lng, r.lat])
      .setHTML(popupFor(r))
      .addTo(state.map);
    state.map.easeTo({
      center: [r.lng, r.lat],
      zoom: Math.max(state.map.getZoom(), 13),
      duration: 700
    });
    markActive(r.id);
    if (fromMap) revealInList(r.id);
  }

  /* map ------------------------------------------------------------------- */
  function addPinLayer(map) {
    map.addSource('hml', { type: 'geojson', data: fc(filtered()) });

    if (map.hasImage('hml-pin')) {
      map.addLayer({
        id: 'hml-pins',
        type: 'symbol',
        source: 'hml',
        layout: {
          'icon-image': 'hml-pin',
          'icon-size': 1,
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      });
    } else {
      // Without the pin image the symbol layer would draw nothing at all, so
      // fall back to a dot rather than an empty map.
      map.addLayer({
        id: 'hml-pins',
        type: 'circle',
        source: 'hml',
        paint: {
          'circle-radius': 6,
          'circle-color': '#C9820A',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });
    }

    map.on('click', 'hml-pins', function (e) {
      var f = e.features && e.features[0];
      if (!f) return;
      var p = f.properties;
      showPoint({
        id: p.id, name: p.name, addr: p.addr, city: p.city,
        hours: p.hours, pickup: p.pickup,
        lng: f.geometry.coordinates[0], lat: f.geometry.coordinates[1]
      }, true);
    });
    map.on('mouseenter', 'hml-pins', function () { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'hml-pins', function () { map.getCanvas().style.cursor = ''; });

    state.ready = true;
    if (els.loading) els.loading.remove();
    var tries = 0;
    (function settle() {
      if (els.map.clientWidth && els.map.clientHeight) { map.resize(); return; }
      if (tries++ < 25) setTimeout(settle, 100);
    })();
  }

  function buildMap() {
    window.mapboxgl.accessToken = CFG.token;
    var map = new window.mapboxgl.Map({
      container: els.map,
      style: CFG.style,
      center: LT_CENTER,
      zoom: LT_ZOOM,
      // Two-finger zoom on touch so the page still scrolls past the map, but a
      // mouse wheel zooms directly: ctrl+scroll just reads as a map that will not zoom.
      cooperativeGestures: window.matchMedia('(hover: none)').matches,
      attributionControl: true
    });
    state.map = map;
    map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    map.on('load', function () {
      // loadImage takes a callback in GL v2 and returns a promise in v3. Accept
      // both, and only ever build the layer once.
      var built = false;
      function done(img) {
        if (built) return;
        built = true;
        try {
          if (img && !map.hasImage('hml-pin')) map.addImage('hml-pin', img, { pixelRatio: 2 });
        } catch (e) { /* an image that will not decode just means the dot fallback */ }
        addPinLayer(map);
      }
      try {
        var ret = map.loadImage(CFG.pin, function (err, img) { done(err ? null : img); });
        if (ret && typeof ret.then === 'function') {
          ret.then(function (r) { done(r && r.data ? r.data : r); }).catch(function () { done(null); });
        }
      } catch (e) {
        done(null);
      }
      // Never leave the overlay up if the image hangs.
      setTimeout(function () { done(null); }, 4000);
    });
    map.on('error', function () {
      if (!state.ready) fail();
    });
  }

  /* interactions ---------------------------------------------------------- */
  function moveThumb() {
    if (!els.thumb || !els.seg) return;
    var active = els.seg.querySelector('[aria-selected="true"]');
    if (!active) return;
    els.thumb.style.width = active.offsetWidth + 'px';
    els.thumb.style.transform = 'translateX(' + (active.offsetLeft - 4) + 'px)';
  }

  function setTab(tab) {
    if (tab === state.tab) return;
    state.tab = tab;
    state.chain = null;
    state.shown = PAGE;
    state.activeId = null;
    if (state.popup) { state.popup.remove(); state.popup = null; }

    var btns = els.seg.querySelectorAll('[data-hml-tab]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].setAttribute('aria-selected', btns[i].getAttribute('data-hml-tab') === tab ? 'true' : 'false');
    }
    moveThumb();

    ensureData(tab).then(function () {
      renderChips();
      renderList();
      syncMap();
      if (state.ready) state.map.easeTo({ center: LT_CENTER, zoom: LT_ZOOM, duration: 700 });
    }).catch(fail);
  }

  root.addEventListener('click', function (e) {
    var tabBtn = e.target.closest('[data-hml-tab]');
    if (tabBtn) return setTab(tabBtn.getAttribute('data-hml-tab'));

    var chip = e.target.closest('[data-hml-chain]');
    if (chip) {
      var c = chip.getAttribute('data-hml-chain');
      state.chain = (!c || state.chain === c) ? null : c;
      state.shown = PAGE;
      var all = root.querySelectorAll('[data-hml-chain]');
      for (var i = 0; i < all.length; i++) {
        var v = all[i].getAttribute('data-hml-chain');
        all[i].setAttribute('aria-pressed', (state.chain ? v === state.chain : !v) ? 'true' : 'false');
      }
      els.list.scrollTop = 0;
      renderList();
      syncMap();
      return;
    }

    if (e.target.closest('[data-hml-more]')) {
      // Keep the panel where it is: re-rendering resets scrollTop otherwise and
      // the reader is thrown back to the first row.
      var keep = els.list.scrollTop;
      state.shown += PAGE;
      renderList();
      els.list.scrollTop = keep;
      return;
    }

    var go = e.target.closest('[data-hml-go]');
    if (go) {
      var row = rowById(go.getAttribute('data-hml-go'));
      if (row && state.ready) showPoint(row, false);
    }
  });

  if (els.search) {
    var debounce;
    els.search.addEventListener('input', function () {
      clearTimeout(debounce);
      debounce = setTimeout(function () {
        state.query = els.search.value;
        state.shown = PAGE;
        renderList();
        syncMap();
      }, 160);
    });
  }

  if (els.near) {
    els.near.addEventListener('click', function () {
      if (!navigator.geolocation) return;
      els.near.setAttribute('aria-busy', 'true');
      navigator.geolocation.getCurrentPosition(function (pos) {
        els.near.removeAttribute('aria-busy');
        state.origin = [pos.coords.longitude, pos.coords.latitude];
        state.shown = PAGE;
        renderList();
        if (state.ready) state.map.easeTo({ center: state.origin, zoom: 11, duration: 900 });
      }, function () {
        els.near.removeAttribute('aria-busy');
      }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 });
    });
  }

  window.addEventListener('resize', moveThumb);

  /* boot ------------------------------------------------------------------ */
  function start() {
    moveThumb();
    ensureData('retail').then(function () {
      renderChips();
      renderList();
      loadGl(buildMap);
    }).catch(fail);
  }

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          io.disconnect();
          start();
          return;
        }
      }
    }, { rootMargin: '300px' });
    io.observe(root);
  } else {
    start();
  }
})();
